/**
 * An organization's rules for the pull requests Airlock opens and the branches
 * it lands on (§5.16, §5.25) — `policy.pullRequests` in its settings.
 *
 *   reviewers / teamReviewers  asked for on every pull request Airlock opens
 *                              in the organization's workspaces (a GitHub
 *                              login; a team as `org/team-slug`)
 *   protectedBranches          globs Airlock's landing by itself (`autoLand`)
 *                              never lands on: `main`, `release/*`, `**`
 *   requiredChecks             check names that must have passed on the branch
 *                              Airlock lands on by itself before it does
 *   titleTemplate / bodyTemplate  `{title}` / `{body}` stand for what the
 *                              person or the agent wrote
 *   draft                      every pull request opened as a draft
 *
 * In the **policy** rather than in `defaults`, because it is not a value a
 * member's machine has one of: it holds on every pull request Airlock opens in
 * the organization's workspaces, from every machine, and nothing beneath it
 * may switch it off — a rule a member could clear is no rule. It reaches the
 * organization's own workspaces only: a member's personal repositories are not
 * the organization's to govern.
 */
import { isPlainObject } from '../workspace/shape';

export interface OrgPullRequests {
  reviewers: string[];
  teamReviewers: string[];
  protectedBranches: string[];
  requiredChecks: string[];
  titleTemplate: string;
  bodyTemplate: string;
  draft: boolean;
}

export const EMPTY_PULL_REQUEST_RULES: OrgPullRequests = {
  reviewers: [],
  teamReviewers: [],
  protectedBranches: [],
  requiredChecks: [],
  titleTemplate: '',
  bodyTemplate: '',
  draft: false
};

/** Enough for any real team, few enough to read. */
export const MAX_PULL_REQUEST_LIST = 30;
export const MAX_TEMPLATE_CHARS = 4000;

const LOGIN = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})$/;
const TEAM = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})\/[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/;
const GLOB = /^[A-Za-z0-9._\-/*]{1,200}$/;
// eslint-disable-next-line no-control-regex -- a control character in a check name is not one.
const CHECK = /^[^\x00-\x1f\x7f]{1,120}$/;

const LISTS: { key: 'reviewers' | 'teamReviewers' | 'protectedBranches' | 'requiredChecks'; test: RegExp; what: string }[] = [
  { key: 'reviewers', test: LOGIN, what: 'a GitHub login' },
  { key: 'teamReviewers', test: TEAM, what: 'a team as org/team-slug' },
  { key: 'protectedBranches', test: GLOB, what: 'a branch name or a glob such as release/*' },
  { key: 'requiredChecks', test: CHECK, what: 'a check’s name as GitHub shows it' }
];

/** One entry of a list as this document keeps it, or why not. */
export function pullRequestEntryProblem(key: (typeof LISTS)[number]['key'], entry: string): string | null {
  const spec = LISTS.find((one) => one.key === key)!;
  return spec.test.test(entry.trim()) ? null : `${entry} is not ${spec.what}.`;
}

/** The rules as a document carries them, entries that do not read dropped — what a machine reads. */
export function pullRequestRulesOf(raw: unknown): OrgPullRequests {
  if (!isPlainObject(raw)) return EMPTY_PULL_REQUEST_RULES;
  const out = { ...EMPTY_PULL_REQUEST_RULES };
  for (const { key, test } of LISTS) {
    const list = raw[key];
    out[key] = Array.isArray(list)
      ? [...new Set(list.filter((one): one is string => typeof one === 'string' && test.test(one.trim())).map((one) => one.trim()))].slice(0, MAX_PULL_REQUEST_LIST)
      : [];
  }
  out.titleTemplate = typeof raw.titleTemplate === 'string' && raw.titleTemplate.includes('{title}') ? raw.titleTemplate.slice(0, 200) : '';
  out.bodyTemplate = typeof raw.bodyTemplate === 'string' && raw.bodyTemplate.includes('{body}') ? raw.bodyTemplate.slice(0, MAX_TEMPLATE_CHARS) : '';
  out.draft = raw.draft === true;
  return out;
}

/** The rules as an administrator wrote them — each refusal a sentence with its path, nothing dropped silently. */
export function parsePullRequestRules(input: unknown, problems: string[]): OrgPullRequests {
  if (input === undefined || input === null) return EMPTY_PULL_REQUEST_RULES;
  const at = 'policy.pullRequests';
  if (!isPlainObject(input)) {
    problems.push(`${at}: Expected an object.`);
    return EMPTY_PULL_REQUEST_RULES;
  }
  const known = [...LISTS.map((one) => one.key), 'titleTemplate', 'bodyTemplate', 'draft'] as string[];
  for (const key of Object.keys(input)) if (!known.includes(key)) problems.push(`${at}.${key}: Not a field this version of Airlock knows.`);
  const out = { ...EMPTY_PULL_REQUEST_RULES };
  for (const { key } of LISTS) {
    const list = input[key];
    if (list === undefined || list === null) continue;
    if (!Array.isArray(list)) {
      problems.push(`${at}.${key}: Expected a list.`);
      continue;
    }
    const kept: string[] = [];
    for (const entry of list) {
      const text = typeof entry === 'string' ? entry.trim() : '';
      const problem = text ? pullRequestEntryProblem(key, text) : `${String(entry)} is empty.`;
      if (problem) problems.push(`${at}.${key}: ${problem}`);
      else if (kept.includes(text)) problems.push(`${at}.${key}: ${text} is listed twice.`);
      else kept.push(text);
    }
    if (kept.length > MAX_PULL_REQUEST_LIST) problems.push(`${at}.${key}: ${MAX_PULL_REQUEST_LIST} at most.`);
    out[key] = kept.slice(0, MAX_PULL_REQUEST_LIST);
  }
  for (const [key, token, max] of [
    ['titleTemplate', '{title}', 200],
    ['bodyTemplate', '{body}', MAX_TEMPLATE_CHARS]
  ] as const) {
    const value = input[key];
    if (value === undefined || value === null || value === '') continue;
    if (typeof value !== 'string') problems.push(`${at}.${key}: Expected text.`);
    else if (!value.includes(token)) problems.push(`${at}.${key}: Put ${token} where what was written goes.`);
    else if (value.length > max) problems.push(`${at}.${key}: ${max} characters at most.`);
    else out[key] = value;
  }
  if (input.draft !== undefined && typeof input.draft !== 'boolean') problems.push(`${at}.draft: Expected true or false.`);
  out.draft = input.draft === true;
  return out;
}

/** Whether a branch is one an organization protects: `*` within one segment, `**` across them. */
export function branchProtected(globs: readonly string[], branch: string): boolean {
  return globs.some((glob) => {
    const pattern = glob
      .split('**')
      .map((part) => part.split('*').map((piece) => piece.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('[^/]*'))
      .join('.*');
    return new RegExp(`^${pattern}$`).test(branch);
  });
}

/**
 * A title or body under its template. One already written to it — the dialog
 * prefilled it, or a second open of the same text — is left as it is, so a
 * template is never applied twice.
 */
export function underTemplate(template: string, token: '{title}' | '{body}', text: string): string {
  if (!template) return text;
  const [before, after = ''] = template.split(token);
  if (text.startsWith(before) && text.endsWith(after) && (before || after)) return text;
  return template.split(token).join(text);
}

/** One check as the provider reports it on a commit. */
export interface CheckOutcome {
  name: string;
  /** `success`, `neutral` and `skipped` pass; anything else, or still running, does not. */
  conclusion: string | null;
}

const PASSED = ['success', 'neutral', 'skipped'];

/** Why a branch may not be landed on yet, or null — each required check absent, running or failed, named. */
export function checksRefusal(required: readonly string[], checks: readonly CheckOutcome[]): string | null {
  const unmet = required.flatMap((name) => {
    const runs = checks.filter((one) => one.name === name);
    if (!runs.length) return [`${name} has not run`];
    if (runs.some((one) => one.conclusion && !PASSED.includes(one.conclusion))) return [`${name} failed`];
    if (runs.some((one) => !one.conclusion)) return [`${name} is still running`];
    return [];
  });
  return unmet.length ? unmet.join(', ') : null;
}
